import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { api } from '@/api';
import { toast } from 'sonner';
import { Mail, Users, Building2, CheckCircle, AlertTriangle, Search } from 'lucide-react';

export default function DistributionSelector({ 
  projectId, 
  documentType, // 'notice', 'claim', 'event'
  documentId,
  onSubmit,
  onCancel,
  onDistributionReady, // New prop for mailto flow
  customSubmitButton // New prop for custom submit button
}) {
  const [professionalTeam, setProfessionalTeam] = useState([]);
  const [internalTeam, setInternalTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [distributionType, setDistributionType] = useState('both');
  const [selectedExternal, setSelectedExternal] = useState([]);
  const [selectedInternal, setSelectedInternal] = useState([]);
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // PM is always mandatory
  const mandatoryDisciplines = ['Project Manager (PM)'];

  useEffect(() => {
    loadTeams();
  }, [projectId]);

  const loadTeams = async () => {
    try {
      const [profResponse, intResponse] = await Promise.all([
        api.professionalTeam.getAll(projectId),
        api.internalTeam.getAll(projectId)
      ]);
      
      setProfessionalTeam(profResponse.data);
      setInternalTeam(intResponse.data);

      // Auto-select default distribution members
      const defaultExternal = profResponse.data
        .filter(m => m.is_default_distribution || m.is_mandatory_recipient)
        .map(m => m.id);
      setSelectedExternal(defaultExternal);

      const defaultInternal = intResponse.data
        .filter(m => m.include_in_all_notices)
        .map(m => m.id);
      setSelectedInternal(defaultInternal);
    } catch (error) {
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllExternal = () => {
    if (selectedExternal.length === professionalTeam.length) {
      // Keep only mandatory
      const mandatoryIds = professionalTeam
        .filter(m => mandatoryDisciplines.includes(m.discipline))
        .map(m => m.id);
      setSelectedExternal(mandatoryIds);
    } else {
      setSelectedExternal(professionalTeam.map(m => m.id));
    }
  };

  const handleSelectAllInternal = () => {
    if (selectedInternal.length === internalTeam.length) {
      setSelectedInternal([]);
    } else {
      setSelectedInternal(internalTeam.map(m => m.id));
    }
  };

  const handleExternalToggle = (memberId, discipline) => {
    // Prevent unchecking mandatory disciplines
    if (mandatoryDisciplines.includes(discipline) && selectedExternal.includes(memberId)) {
      toast.error(`${discipline} is a mandatory recipient and cannot be removed`);
      return;
    }

    setSelectedExternal(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleInternalToggle = (memberId) => {
    setSelectedInternal(prev => 
      prev.includes(memberId) 
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const validateDistribution = () => {
    const errors = [];

    if (distributionType === 'external' || distributionType === 'both') {
      // Check mandatory recipients
      for (const discipline of mandatoryDisciplines) {
        const hasDiscipline = professionalTeam.some(
          m => m.discipline === discipline && selectedExternal.includes(m.id)
        );
        if (!hasDiscipline) {
          // Check if discipline exists in team
          const disciplineExists = professionalTeam.some(m => m.discipline === discipline);
          if (disciplineExists) {
            errors.push(`${discipline} is mandatory and must be selected`);
          } else {
            errors.push(`No ${discipline} found in professional team. Please add one first.`);
          }
        }
      }
    }

    if (distributionType === 'internal' && selectedInternal.length === 0) {
      errors.push('Please select at least one internal recipient');
    }

    if (distributionType === 'external' && selectedExternal.length === 0) {
      errors.push('Please select at least one external recipient');
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateDistribution();
    if (errors.length > 0) {
      errors.forEach(e => toast.error(e));
      return;
    }

    setSubmitting(true);

    try {
      const additionalEmailList = additionalEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));

      const distribution = {
        distribution_type: distributionType,
        internal_recipients: distributionType === 'external' ? [] : selectedInternal,
        external_recipients: distributionType === 'internal' ? [] : selectedExternal,
        additional_emails: additionalEmailList
      };

      // If onDistributionReady is provided (mailto flow), use that instead
      if (onDistributionReady) {
        onDistributionReady(distribution);
        return;
      }

      const result = await api.correspondence.submit(projectId, documentType, documentId, distribution);
      
      toast.success('Correspondence submitted successfully');
      
      if (onSubmit) {
        onSubmit(result.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit correspondence');
    } finally {
      setSubmitting(false);
    }
  };

  // Group professional team by discipline
  const groupedProfessional = professionalTeam.reduce((acc, member) => {
    const discipline = member.discipline || 'Other';
    if (!acc[discipline]) acc[discipline] = [];
    acc[discipline].push(member);
    return acc;
  }, {});

  // Filter based on search
  const filteredGrouped = searchQuery 
    ? Object.entries(groupedProfessional).reduce((acc, [discipline, members]) => {
        const filtered = members.filter(m => 
          m.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.primary_contact?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.primary_contact?.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          discipline.toLowerCase().includes(searchQuery.toLowerCase())
        );
        if (filtered.length > 0) acc[discipline] = filtered;
        return acc;
      }, {})
    : groupedProfessional;

  const filteredInternal = searchQuery
    ? internalTeam.filter(m =>
        m.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.surname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.role?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : internalTeam;

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Loading distribution options...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="text-xl font-barlow font-bold uppercase">Distribution Selection</h2>
        <p className="text-sm text-slate-500 mt-1">
          Select recipients for this {documentType}
        </p>
      </div>

      {/* Distribution Type Selection */}
      <div className="bg-slate-50 p-4 rounded-sm">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3 block">
          Step 1: Choose Distribution Type
        </Label>
        <RadioGroup 
          value={distributionType} 
          onValueChange={setDistributionType}
          className="flex gap-6"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="internal" id="internal" />
            <Label htmlFor="internal" className="cursor-pointer flex items-center gap-2">
              <Users className="h-4 w-4" />
              Internal Only
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="external" id="external" />
            <Label htmlFor="external" className="cursor-pointer flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              External Only
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="both" id="both" />
            <Label htmlFor="both" className="cursor-pointer flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Internal + External
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by name, company, or discipline..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 rounded-sm"
        />
      </div>

      {/* Mandatory Recipients Warning */}
      {(distributionType === 'external' || distributionType === 'both') && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-700">
            <strong>Mandatory Recipients:</strong> {mandatoryDisciplines.join(', ')} must be selected for all submissions.
          </AlertDescription>
        </Alert>
      )}

      {/* Professional Team Selection */}
      {(distributionType === 'external' || distributionType === 'both') && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
              Step 2: Select External Recipients (Professional Team)
            </Label>
            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline" 
                size="sm"
                onClick={handleSelectAllExternal}
              >
                {selectedExternal.length === professionalTeam.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
          </div>

          {Object.keys(filteredGrouped).length === 0 ? (
            <div className="bg-slate-50 p-6 text-center rounded-sm">
              <Building2 className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-600">No professional team members found.</p>
              <p className="text-sm text-slate-500">Add consultants to the project first.</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[300px] overflow-y-auto border border-slate-200 rounded-sm p-4">
              {Object.entries(filteredGrouped).map(([discipline, members]) => {
                const isMandatory = mandatoryDisciplines.includes(discipline);
                return (
                  <div key={discipline} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-bold text-slate-700">{discipline}</h4>
                      {isMandatory && (
                        <Badge className="text-xs bg-orange-100 text-orange-700 border-orange-200">
                          Mandatory
                        </Badge>
                      )}
                    </div>
                    {members.map(member => (
                      <div 
                        key={member.id}
                        className={`flex items-center space-x-3 p-2 rounded hover:bg-slate-50 ${
                          selectedExternal.includes(member.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <Checkbox
                          id={`ext-${member.id}`}
                          checked={selectedExternal.includes(member.id)}
                          onCheckedChange={() => handleExternalToggle(member.id, member.discipline)}
                          disabled={isMandatory && selectedExternal.includes(member.id)}
                        />
                        <label 
                          htmlFor={`ext-${member.id}`} 
                          className="flex-1 cursor-pointer text-sm"
                        >
                          <span className="font-medium">{member.company_name}</span>
                          <span className="text-slate-500 ml-2">
                            - {member.primary_contact?.name} {member.primary_contact?.surname}
                          </span>
                          {member.primary_contact?.email && (
                            <span className="text-slate-400 ml-2 text-xs">
                              ({member.primary_contact.email})
                            </span>
                          )}
                        </label>
                        {member.is_default_distribution && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-sm text-slate-500">
            {selectedExternal.length} external recipient(s) selected
          </div>
        </div>
      )}

      {/* Internal Team Selection */}
      {(distributionType === 'internal' || distributionType === 'both') && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {distributionType === 'both' ? 'Step 3:' : 'Step 2:'} Select Internal Recipients
            </Label>
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={handleSelectAllInternal}
            >
              {selectedInternal.length === internalTeam.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {filteredInternal.length === 0 ? (
            <div className="bg-slate-50 p-6 text-center rounded-sm">
              <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-600">No internal team members found.</p>
              <p className="text-sm text-slate-500">Add internal team members to the project first.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-[200px] overflow-y-auto border border-slate-200 rounded-sm p-4">
              {filteredInternal.map(member => (
                <div 
                  key={member.id}
                  className={`flex items-center space-x-3 p-2 rounded hover:bg-slate-50 ${
                    selectedInternal.includes(member.id) ? 'bg-green-50' : ''
                  }`}
                >
                  <Checkbox
                    id={`int-${member.id}`}
                    checked={selectedInternal.includes(member.id)}
                    onCheckedChange={() => handleInternalToggle(member.id)}
                  />
                  <label 
                    htmlFor={`int-${member.id}`} 
                    className="flex-1 cursor-pointer text-sm"
                  >
                    <span className="font-medium">{member.name} {member.surname}</span>
                    <span className="text-slate-500 ml-2">- {member.role}</span>
                    {member.email && (
                      <span className="text-slate-400 ml-2 text-xs">({member.email})</span>
                    )}
                  </label>
                  {member.include_in_all_notices && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">All Notices</Badge>
                  )}
                  {member.internal_only && (
                    <Badge variant="outline" className="text-xs">CC</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="text-sm text-slate-500">
            {selectedInternal.length} internal recipient(s) selected
          </div>
        </div>
      )}

      {/* Additional Emails */}
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
          Additional Email Recipients (comma separated)
        </Label>
        <Input
          value={additionalEmails}
          onChange={(e) => setAdditionalEmails(e.target.value)}
          placeholder="email1@example.com, email2@example.com"
          className="rounded-sm"
        />
      </div>

      {/* Summary */}
      <div className="bg-slate-100 p-4 rounded-sm">
        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2">Distribution Summary</h4>
        <div className="text-sm space-y-1">
          <p><strong>Type:</strong> {distributionType === 'both' ? 'Internal + External' : distributionType}</p>
          {(distributionType === 'external' || distributionType === 'both') && (
            <p><strong>External (TO):</strong> {selectedExternal.length} recipient(s)</p>
          )}
          {(distributionType === 'internal' || distributionType === 'both') && (
            <p><strong>Internal:</strong> {selectedInternal.length} recipient(s)</p>
          )}
          {additionalEmails && (
            <p><strong>Additional:</strong> {additionalEmails.split(',').filter(e => e.trim()).length} email(s)</p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        {onCancel && (
          <Button 
            type="button" 
            variant="outline" 
            onClick={onCancel}
            className="flex-1 rounded-sm"
          >
            Cancel
          </Button>
        )}
        {customSubmitButton ? (
          <div className="flex-1" onClick={handleSubmit}>
            {customSubmitButton}
          </div>
        ) : (
          <Button 
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
          >
            {submitting ? 'Submitting...' : 'Submit Correspondence'}
          </Button>
        )}
      </div>

      {!onDistributionReady && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
          <p className="text-xs text-amber-700">
            <strong>Note:</strong> Email sending is currently <strong>MOCKED</strong> for testing purposes. 
            No actual emails will be sent.
          </p>
        </div>
      )}
    </div>
  );
}
